/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, createContext, useCallback, useMemo, useState } from "react";
import {
  ChakraModal,
  ChakraModalContent as ModalContent,
  ChakraModalOverlay as ModalOverlay,
  chakraUseDisclosure as useDisclosure,
  chakraUseBreakpointValue as useBreakpointValue,
} from "@airhelp/react-ui-components";
import type { ReactNode } from "react";
import { useRouter } from "next/router";
import { trackUAEvent, trackCustomEvent } from "utils/tracking";
import type { ModalContextProviderProps, ModalContextProps } from "./ModalContext.d";

export const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const ModalContextProvider = ({ children, gaEvent, uaEvent }: ModalContextProviderProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalContent, setModalContent] = useState<ReactNode>(null);
  const [size, setModalSize] = useState<string>();
  const router = useRouter();

  const handleHashChange = () => {
    onClose();
    trackUAEvent(uaEvent);
    trackCustomEvent(gaEvent);

    window.removeEventListener("hashchange", handleHashChange);
  };

  useEffect(
    () => () => {
      window.removeEventListener("hashchange", handleHashChange);
    },
    []
  );

  const openModal: ModalContextProps["openModal"] = useCallback(
    (content, modalSize = "sm") => {
      if (content) {
        onOpen();
        setModalContent(content);
        setModalSize(modalSize);
        const pathToPush = router.asPath.includes("#") ? `${router.asPath}modal` : `${router.asPath}#modal`;
        router.push(pathToPush, null, { shallow: true });
        window.addEventListener("hashchange", handleHashChange);
      }
    },
    [onOpen, router]
  );

  const closeModal = useCallback(() => {
    onClose();
    router?.push(`${router.asPath}#`, null, { shallow: true });
    trackUAEvent(uaEvent);
    trackCustomEvent(gaEvent);
    window.removeEventListener("hashchange", handleHashChange);
  }, [onClose, gaEvent]);

  const value = useMemo(
    () => ({
      openModal,
      closeModal,
      onClose,
    }),
    [openModal, closeModal]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      <ChakraModal
        isOpen={isOpen}
        onClose={closeModal}
        size={useBreakpointValue({ base: "full", md: size })}
        isCentered={false}
      >
        <ModalOverlay bg="rgba(69, 84, 115, 0.7)" />
        <ModalContent
          borderRadius="md"
          overflow="auto"
          minHeight={{ base: "auto", md: "initial" }}
          mt={{ base: 0, md: size === "full" ? 4 : 12 }}
          {...(size === "full" ? { mb: 0 } : null)}
          {...(size === "xl" ? { maxWidth: "755px" } : null)}
        >
          {modalContent}
        </ModalContent>
      </ChakraModal>
    </ModalContext.Provider>
  );
};
